// The "What could help" hub lists — one component serving both layouts so a
// copy or accessibility fix can never land in one and miss the other.
// Mobile: plain rows with the teal "Open" affordance (M/List row · Open).
// Desktop: level-tinted group heads and numbered rows whose › opens the
// flyout (D/Hub row); the rank number is the matcher's order, priority first.
import React from 'react';
import { levelBg, Mark } from './levels';

const BUCKETS = [
  { key: 'priority', level: 'priority', label: 'Priority areas' },
  { key: 'support', level: 'support', label: 'Support needs' },
];

export default function HelpHubList({ help, setting, desktop = false, onOpen }) {
  // Continuous rank across priority-then-support, matching the flyout order.
  const ranks = new Map(
    [...help.priority, ...help.support].map((group, i) => [group, i])
  );

  return (
    <>
      {BUCKETS.map((bucket) => {
        const groups = help[bucket.key];
        if (groups.length === 0) return null;
        return (
          <React.Fragment key={bucket.key}>
            {!desktop && (
              <div className="flex items-center gap-[8px] pl-[4px] pt-[16px] w-full">
                <h2 className="hfp-heading-18 whitespace-nowrap">{bucket.label}</h2>
              </div>
            )}
            <div
              className={`flex w-full flex-col overflow-hidden rounded-[var(--hfp-radius-16)] border border-solid bg-[var(--hfp-surface-card)] ${
                desktop ? 'border-[color:var(--hfp-line-border)]' : 'border-[color:var(--hfp-line-hair)]'
              }`}
              style={{ boxShadow: desktop ? 'var(--hfp-shadow-card-desktop)' : 'var(--hfp-shadow-card)' }}
            >
              {desktop && (
                <div
                  className="flex items-center gap-[8px] w-full px-[20px] py-[12px]"
                  style={{ background: levelBg(bucket.level) }}
                >
                  <Mark level={bucket.level} size={16} />
                  <span className="hfp-meta-strong-14 text-[color:var(--hfp-ink-strong)] whitespace-nowrap">
                    {bucket.label} at {setting}
                  </span>
                </div>
              )}
              {groups.map((group, i) => (
                <React.Fragment key={group.areaName}>
                  {i > 0 && (
                    <span aria-hidden="true" className="h-px w-full bg-[var(--hfp-line-hair)]" />
                  )}
                  {desktop ? (
                    <button
                      type="button"
                      onClick={() => onOpen(group, ranks.get(group))}
                      className="flex w-full items-center gap-[12px] px-[20px] py-[14px] text-left bg-[var(--hfp-surface-card)]"
                    >
                      <span className="flex items-center justify-center size-[28px] shrink-0 rounded-[var(--hfp-radius-8)] bg-[var(--hfp-surface-head-plum)]">
                        <span className="hfp-label-14 text-[color:var(--hfp-ink-heading)]">{ranks.get(group) + 1}</span>
                      </span>
                      <span className="hfp-sub-16 flex-1 min-w-0 text-[color:var(--hfp-ink-heading)]">{group.areaName}</span>
                      <span className="hfp-heading-20 !text-[color:var(--hfp-ink-faint)]" aria-hidden="true">›</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onOpen(group, ranks.get(group))}
                      className="flex h-[64px] w-full items-center gap-[12px] px-[16px] rounded-[var(--hfp-radius-8)] text-left"
                    >
                      <span className="hfp-sub-16 flex-1 min-w-0 text-[color:var(--hfp-ink-strong)]">{group.areaName}</span>
                      <span className="hfp-body-strong-16 underline text-[color:var(--hfp-link-teal)] whitespace-nowrap">Open</span>
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
          </React.Fragment>
        );
      })}
      {desktop && (
        <p className="hfp-meta-14 w-full text-[color:var(--hfp-ink-faint)]">
          Open any area to see specific strategies, why they matter and how they might help.
        </p>
      )}
    </>
  );
}
